import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, ShoppingBag, Sparkles, ImageIcon, BookOpen, FileText, Package, Inbox, Ticket, ArrowRight } from "lucide-react";

/**
 * Reusable, keyboard-accessible autocomplete search bar.
 *
 * Props:
 *   - onQuery(query): async (query: string) => Promise<{results, ...}>
 *       Return an array of items with { type, id?, title, subtitle?, url, image? } or
 *       an array of groups [{label, items:[...]}] to render them grouped.
 *   - placeholder: string
 *   - onSelect(item): called when a result is picked (navigates to item.url by default)
 *   - variant: "hero" | "compact" | "admin"    (visual styles)
 *   - autoFocus, testId, className
 *   - noResultsHint: string
 */
const ICONS = {
  product: ShoppingBag,
  service: Sparkles,
  gallery: ImageIcon,
  blog: BookOpen,
  page: FileText,
  order: Package,
  quote: Inbox,
  coupon: Ticket,
};

export default function Autocomplete({
  onQuery,
  placeholder = "Search…",
  onSelect,
  variant = "compact",
  autoFocus = false,
  testId = "autocomplete",
  className = "",
  noResultsHint = "No matches — try a different keyword.",
  minQueryLength = 1,
  debounceMs = 220,
  value,          // controlled input value (optional)
  onChange,       // controlled input change handler (optional)
}) {
  const [uncontrolledQ, setUncontrolledQ] = useState("");
  const isControlled = value !== undefined;
  const q = isControlled ? value : uncontrolledQ;
  const setQ = (v) => {
    if (isControlled) onChange?.(v);
    else setUncontrolledQ(v);
  };
  const [groups, setGroups] = useState([]);   // [{label, items:[]}]
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);   // flat index into visible items
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);
  const navigate = useNavigate();

  // Flatten for keyboard navigation.
  const flatItems = useMemo(() => {
    const flat = [];
    for (const g of groups) for (const it of g.items || []) flat.push(it);
    return flat;
  }, [groups]);

  // Debounced query.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < minQueryLength) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const res = await onQuery(trimmed);
        // Ignore stale responses.
        if (seq !== requestSeq.current) return;
        // Normalise response into groups.
        if (Array.isArray(res)) {
          setGroups([{ label: null, items: res }]);
        } else if (res && Array.isArray(res.results)) {
          setGroups([{ label: null, items: res.results }]);
        } else if (res && Array.isArray(res.groups)) {
          setGroups(res.groups);
        } else {
          setGroups([]);
        }
      } catch {
        setGroups([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, debounceMs);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, onQuery, minQueryLength, debounceMs]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleSelect = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    // Only clear the query when uncontrolled — controlled parents decide their own
    // post-selection state (e.g. AdminCatalog keeps the filter to show just the picked row).
    if (!isControlled) setUncontrolledQ("");
    if (onSelect) onSelect(item);
    else if (item.url) navigate(item.url);
  }, [navigate, onSelect, isControlled]);

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) setOpen(true);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && flatItems[active]) handleSelect(flatItems[active]);
      else if (flatItems[0]) handleSelect(flatItems[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      inputRef.current?.blur();
    }
  };

  useEffect(() => { setActive(-1); }, [q]);

  const styles = STYLE_BY_VARIANT[variant] || STYLE_BY_VARIANT.compact;
  const showDropdown = open && q.trim().length >= minQueryLength;

  let itemIndex = -1;

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <div className={`flex items-center gap-2 ${styles.wrapper}`}>
        <Search size={variant === "hero" ? 18 : 14} className={styles.icon} />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q.trim().length >= minQueryLength && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          data-testid={`${testId}-input`}
          className={`bg-transparent flex-1 outline-none ${styles.input}`}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <Loader2 size={14} className="animate-spin text-white/40 shrink-0" />}
        {q && !loading && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); inputRef.current?.focus(); }}
            data-testid={`${testId}-clear`}
            className="text-white/40 hover:text-white shrink-0"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className={`absolute left-0 right-0 mt-2 z-[60] ${styles.dropdown} max-h-[420px] overflow-y-auto`}
          role="listbox"
          data-testid={`${testId}-dropdown`}
        >
          {loading && flatItems.length === 0 ? (
            <div className="px-4 py-3 text-white/50 text-sm">Searching…</div>
          ) : flatItems.length === 0 ? (
            <div className="px-4 py-4 text-white/50 text-sm" data-testid={`${testId}-empty`}>{noResultsHint}</div>
          ) : (
            groups.map((g, gi) => (
              <div key={gi}>
                {g.label && (
                  <div className="px-4 pt-3 pb-1.5 text-[9px] uppercase tracking-[0.22em] text-white/40">{g.label}</div>
                )}
                {g.items.map((it) => {
                  itemIndex += 1;
                  const isActive = itemIndex === active;
                  const Icon = ICONS[it.type] || Search;
                  const key = `${it.type || "x"}-${it.id ?? it.url}-${itemIndex}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      onMouseEnter={() => setActive(itemIndex)}
                      onClick={() => handleSelect(it)}
                      data-testid={`${testId}-item-${it.type || "x"}-${it.id ?? itemIndex}`}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors border-l-2 ${isActive ? "bg-gold/10 border-gold" : "border-transparent hover:bg-white/[0.02]"}`}
                    >
                      <div className={`w-8 h-8 rounded-sm bg-ink-800 border border-white/5 flex items-center justify-center overflow-hidden shrink-0 ${it.image ? "" : "p-1.5"}`}>
                        {it.image ? (
                          <img src={it.image.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${it.image}` : it.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <Icon size={13} className="text-gold" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{it.title}</div>
                        {it.subtitle && (
                          <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 truncate mt-0.5">
                            {it.type ? `${it.type} · ` : ""}{it.subtitle}
                          </div>
                        )}
                      </div>
                      <ArrowRight size={12} className={isActive ? "text-gold" : "text-white/30"} />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const STYLE_BY_VARIANT = {
  hero: {
    wrapper: "bg-white/[0.04] backdrop-blur-md border border-white/10 hover:border-gold/40 focus-within:border-gold px-5 py-4 rounded-sm transition-colors",
    icon: "text-gold shrink-0",
    input: "text-white text-base placeholder:text-white/40",
    dropdown: "card-dark backdrop-blur-xl shadow-2xl",
  },
  compact: {
    wrapper: "bg-ink-800 border border-white/10 focus-within:border-gold px-3 py-2 rounded-sm transition-colors",
    icon: "text-white/50 shrink-0",
    input: "text-white text-sm placeholder:text-white/40",
    dropdown: "card-dark shadow-xl",
  },
  admin: {
    wrapper: "bg-ink-900 border border-white/10 focus-within:border-gold px-3 py-2.5 rounded-sm transition-colors",
    icon: "text-white/50 shrink-0",
    input: "text-white text-sm placeholder:text-white/45",
    dropdown: "card-dark shadow-xl",
  },
};
